import type { HeightmapAsset, PatternLayer, ReliefFinish, WallMetrics } from '@/core/types';
import { createHeightField } from '@/core/heightfield';
import { assetTable } from '@/core/heightfield';
import { defaultFinish } from '@/core/design/defaults';

/**
 * Height field thumbnails.
 *
 * Preset cards and pattern pickers are drawn by the same evaluator that builds
 * the mesh, so a thumbnail can never show something the exported panel will not
 * have. Shading comes from the height gradient, which is what makes a relief
 * legible at 96 pixels where a flat grayscale map would not be.
 */

export interface PreviewOptions {
  /** How many panels across to show; more than one reveals multi-panel motifs. */
  panels?: number;
  /** Nominal panel size used for the preview's wall metrics, in millimetres. */
  panelSize?: number;
  finish?: ReliefFinish;
  assets?: readonly HeightmapAsset[];
  /** Relief exaggeration for shading only, relative to the panel size. */
  relief?: number;
  light?: readonly [number, number, number];
  base?: readonly [number, number, number];
}

function normaliseVector(x: number, y: number, z: number): [number, number, number] {
  const length = Math.hypot(x, y, z) || 1;
  return [x / length, y / length, z / length];
}

/** Samples the field into a square grid of normalised heights. */
export function sampleHeightGrid(
  layers: readonly PatternLayer[],
  resolution: number,
  options: PreviewOptions = {},
): Float32Array {
  const panels = Math.max(1, options.panels ?? 2);
  const panelSize = options.panelSize ?? 150;
  const extent = panels * panelSize;

  const wall: WallMetrics = {
    columns: panels,
    rows: panels,
    width: extent,
    height: extent,
    pitchX: panelSize,
    pitchY: panelSize,
    panelSize,
  };

  const panelRefs = [];
  for (let row = 0; row < panels; row += 1) {
    for (let column = 0; column < panels; column += 1) {
      panelRefs.push({
        column,
        row,
        centerX: panelSize / 2 + column * panelSize,
        centerY: panelSize / 2 + row * panelSize,
        size: panelSize,
        rotation: 0,
      });
    }
  }

  const field = createHeightField({
    layers,
    finish: options.finish ?? defaultFinish(),
    wall,
    footprint: {
      minX: -panelSize / 2,
      minY: -panelSize / 2,
      maxX: panelSize / 2,
      maxY: panelSize / 2,
    },
    assets: assetTable(options.assets ?? []),
    panels: panelRefs,
  });

  const grid = new Float32Array(resolution * resolution);
  for (let j = 0; j < resolution; j += 1) {
    // Row 0 of the output is the top of the image, so walk wall space downwards.
    const y = ((resolution - 1 - j) / (resolution - 1)) * extent;
    const row = Math.min(panels - 1, Math.floor(y / panelSize));
    for (let i = 0; i < resolution; i += 1) {
      const x = (i / (resolution - 1)) * extent;
      const column = Math.min(panels - 1, Math.floor(x / panelSize));
      grid[j * resolution + i] = field.sample(x, y, panelRefs[row * panels + column]);
    }
  }

  return grid;
}

/** Shades a height grid into RGBA pixels using its own gradient as a normal. */
export function shadeHeightGrid(
  grid: Float32Array,
  resolution: number,
  options: PreviewOptions = {},
): ImageData {
  const image = new ImageData(resolution, resolution);
  const light = normaliseVector(
    ...((options.light ?? [-0.45, 0.6, 0.66]) as [number, number, number]),
  );
  const base = options.base ?? [204, 212, 220];
  // Gradient strength: how many height units one pixel step is worth.
  const relief = (options.relief ?? 2.2) * resolution * 0.01;

  const at = (i: number, j: number): number =>
    grid[
      Math.min(resolution - 1, Math.max(0, j)) * resolution +
        Math.min(resolution - 1, Math.max(0, i))
    ];

  for (let j = 0; j < resolution; j += 1) {
    for (let i = 0; i < resolution; i += 1) {
      const dx = (at(i + 1, j) - at(i - 1, j)) * relief;
      // Image rows run downwards while the height field runs upwards.
      const dy = (at(i, j - 1) - at(i, j + 1)) * relief;
      const [nx, ny, nz] = normaliseVector(-dx, -dy, 1);

      const lambert = Math.max(0, nx * light[0] + ny * light[1] + nz * light[2]);
      const height = at(i, j);
      // A touch of ambient occlusion by height keeps valleys from flattening out.
      const shade = 0.22 + 0.62 * lambert + 0.16 * height;

      const offset = (j * resolution + i) * 4;
      image.data[offset] = Math.min(255, base[0] * shade);
      image.data[offset + 1] = Math.min(255, base[1] * shade);
      image.data[offset + 2] = Math.min(255, base[2] * shade);
      image.data[offset + 3] = 255;
    }
  }

  return image;
}

export function renderHeightPreview(
  canvas: HTMLCanvasElement,
  layers: readonly PatternLayer[],
  options: PreviewOptions = {},
): void {
  const resolution = canvas.width;
  const context = canvas.getContext('2d');
  if (!context || resolution === 0) return;

  const grid = sampleHeightGrid(layers, resolution, options);
  context.putImageData(shadeHeightGrid(grid, resolution, options), 0, 0);
}

/** Renders once into an off-screen canvas and returns a cacheable data URL. */
export function renderPreviewDataUrl(
  layers: readonly PatternLayer[],
  resolution: number,
  options: PreviewOptions = {},
): string {
  const canvas = document.createElement('canvas');
  canvas.width = resolution;
  canvas.height = resolution;
  renderHeightPreview(canvas, layers, options);
  return canvas.toDataURL('image/png');
}
